import { KanbanComprasData, PedidoCard } from "@/services/suprimentos";
import { currentDatePeriod } from "@/utils/dateInput";
import {
  GameZone,
  SupplierCreature,
  BattleEncounter,
  SupplierTier,
} from "./gameTypes";

const CREATURE_SPRITES = [
  "🏗️", "🏢", "🔩", "⚙️", "🧱", "🪵", "🔧", "🪨",
  "🔑", "📦", "🚚", "🏭", "🪛", "🛠️", "🪜",
];

function deterministicEmoji(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  return CREATURE_SPRITES[Math.abs(hash) % CREATURE_SPRITES.length];
}

function calcLevel(value: number, maxValue: number): number {
  if (maxValue <= 0 || value <= 0) return 1;
  const ratio = Math.log(value + 1) / Math.log(maxValue + 1);
  return Math.max(1, Math.min(50, Math.round(ratio * 50)));
}

function supplierTier(avgValue: number): {
  tier: SupplierTier;
  tierLabel: string;
  tierColor: string;
  tierEmoji: string;
} {
  if (avgValue >= 100_000)
    return { tier: "lendario", tierLabel: "Lendário", tierColor: "yellow", tierEmoji: "⭐" };
  if (avgValue >= 50_000)
    return { tier: "raro", tierLabel: "Raro", tierColor: "purple", tierEmoji: "💎" };
  if (avgValue >= 10_000)
    return { tier: "incomum", tierLabel: "Incomum", tierColor: "blue", tierEmoji: "🔷" };
  return { tier: "comum", tierLabel: "Comum", tierColor: "gray", tierEmoji: "⚪" };
}

export function buildGameState(
  kanban: KanbanComprasData
): Omit<import("./gameTypes").GameState, "loading" | "error"> {
  const {
    solPendente,
    solAprovada,
    cotacoes,
    pedidosSemFinanceiro,
    pedidosGeradoFinanceiro,
  } = kanban;

  const zones: GameZone[] = [
    {
      id: "solPendente",
      name: "FRONTEIRA SELVAGEM",
      emoji: "🌿",
      description: "Solicitações pendentes de aprovação",
      count: solPendente.length,
      totalValue: 0,
      isBoss: false,
      isSafe: false,
      colorKey: solPendente.length > 5 ? "red" : solPendente.length > 0 ? "yellow" : "gray",
    },
    {
      id: "solAprovada",
      name: "CAMINHO APROVADO",
      emoji: "✅",
      description: "Solicitações aprovadas indo para cotação",
      count: solAprovada.length,
      totalValue: 0,
      isBoss: false,
      isSafe: false,
      colorKey: "blue",
    },
    {
      id: "cotacoes",
      name: "MERCADO DOS FORNECEDORES",
      emoji: "🏪",
      description: "Cotações em negociação",
      count: cotacoes.length,
      totalValue: 0,
      isBoss: false,
      isSafe: false,
      colorKey: "purple",
    },
    {
      id: "pedidosSemFinanceiro",
      name: "CAVERNA DO CHEFE",
      emoji: pedidosSemFinanceiro.length > 0 ? "⚠️" : "🏚️",
      description: "Pedidos sem financeiro gerado",
      count: pedidosSemFinanceiro.length,
      totalValue: pedidosSemFinanceiro.reduce((s, p) => s + (p.value ?? 0), 0),
      isBoss: pedidosSemFinanceiro.length > 0,
      isSafe: false,
      colorKey: pedidosSemFinanceiro.length > 0 ? "red" : "gray",
    },
    {
      id: "pedidosGeradoFinanceiro",
      name: "COFRE DA VITÓRIA",
      emoji: "💰",
      description: "Pedidos com financeiro gerado",
      count: pedidosGeradoFinanceiro.length,
      totalValue: pedidosGeradoFinanceiro.reduce((s, p) => s + (p.value ?? 0), 0),
      isBoss: false,
      isSafe: true,
      colorKey: "green",
    },
  ];

  // Aggregate suppliers from pedidos
  interface SupplierAgg {
    orders: PedidoCard[];
    isCapturado: boolean;
    hasPendingFinanceiro: boolean;
  }
  const supplierMap = new Map<string, SupplierAgg>();

  for (const p of pedidosSemFinanceiro) {
    const k = (p.supplier || "—").trim();
    const existing = supplierMap.get(k) ?? { orders: [], isCapturado: false, hasPendingFinanceiro: false };
    existing.orders.push(p);
    existing.hasPendingFinanceiro = true;
    supplierMap.set(k, existing);
  }
  for (const p of pedidosGeradoFinanceiro) {
    const k = (p.supplier || "—").trim();
    const existing = supplierMap.get(k) ?? { orders: [], isCapturado: false, hasPendingFinanceiro: false };
    existing.orders.push(p);
    existing.isCapturado = true;
    supplierMap.set(k, existing);
  }

  const maxValue = Math.max(
    1,
    ...Array.from(supplierMap.values()).map((v) =>
      v.orders.reduce((s, o) => s + (o.value ?? 0), 0)
    )
  );

  const creatures: SupplierCreature[] = Array.from(supplierMap.entries())
    .map(([name, data]) => {
      const total = data.orders.reduce((s, o) => s + (o.value ?? 0), 0);
      const avg = total / Math.max(1, data.orders.length);
      const tierInfo = supplierTier(avg);
      const level = calcLevel(total, maxValue);
      const atk = data.hasPendingFinanceiro
        ? Math.min(99, 55 + Math.round((total / maxValue) * 44))
        : Math.max(1, Math.round((total / maxValue) * 60));
      const def = data.isCapturado ? Math.min(99, 50 + Math.round((level / 50) * 49)) : 30;
      const vel = Math.min(99, Math.max(10, data.orders.length * 8));
      return {
        id: name,
        name: name.length > 26 ? name.slice(0, 24) + "…" : name,
        level,
        ...tierInfo,
        ordersCount: data.orders.length,
        totalValue: total,
        avgOrderValue: avg,
        isCapturado: data.isCapturado,
        hasPendingFinanceiro: data.hasPendingFinanceiro,
        sprite: deterministicEmoji(name),
        stats: {
          ataque: Math.max(1, Math.min(99, atk)),
          defesa: Math.max(1, Math.min(99, def)),
          velocidade: Math.max(1, Math.min(99, vel)),
        },
      };
    })
    .sort((a, b) => b.totalValue - a.totalValue);

  // Encounters = solPendente SCs
  const encounters: BattleEncounter[] = solPendente.map((sc) => ({
    purchaseRequestId: sc.purchaseRequestId,
    buildingCode: sc.buildingCode,
    buildingName: sc.buildingName,
    costCenterName: sc.costCenterName,
    itemCount: sc.itemCount,
    requestDate: sc.requestDate,
    maxCompetenceLevel: sc.maxCompetenceLevel ?? 0,
    isBoss: false,
    itemsPreview: sc.itemsPreview ?? [],
  }));

  const totalOrdersValue =
    pedidosSemFinanceiro.reduce((s, p) => s + (p.value ?? 0), 0) +
    pedidosGeradoFinanceiro.reduce((s, p) => s + (p.value ?? 0), 0);
  const totalOrdersCount = pedidosSemFinanceiro.length + pedidosGeradoFinanceiro.length;
  const playerXp = pedidosGeradoFinanceiro.length * 10 + encounters.length * 3;
  const playerLevel = Math.max(1, Math.floor(Math.sqrt(playerXp / 5)) + 1);

  return {
    zones,
    creatures,
    encounters,
    totalOrdersValue,
    totalOrdersCount,
    playerLevel,
    playerXp,
  };
}

export function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("pt-BR");
  } catch {
    return dateStr;
  }
}

export function statBar(value: number): string {
  const filled = Math.round((value / 99) * 10);
  return "█".repeat(filled) + "░".repeat(10 - filled);
}

export function hpColor(pct: number): string {
  if (pct >= 50) return "bg-emerald-500";
  if (pct >= 25) return "bg-yellow-500";
  return "bg-red-500";
}

export function defaultGamePeriod(): { startDate: string; endDate: string } {
  return currentDatePeriod();
}
